import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Cloud,
  Copy,
  ExternalLink,
  Folder,
  Image as ImageIcon,
  Loader2,
  Pencil,
  RefreshCw,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { CDN_UPLOAD_MAX_BYTES } from '../../lib/subscriberCustomFields';
import { captureEdgeFunctionFailure } from '../../sentry';
import {
  cdnFolderSearch,
  cdnObjectAlreadyExists,
  deleteCdnObject,
  folderName,
  formatBytes,
  isImageKey,
  joinObjectKey,
  listCdnObjects,
  listCdnProfiles,
  normalizeBrowsePrefix,
  objectDir,
  objectFilename,
  renameCdnObject,
  uploadCdnObject,
} from './api';
import type { CdnObject, CdnProfile } from './types';

const MAX_UPLOAD_MB = CDN_UPLOAD_MAX_BYTES / (1024 * 1024);

export default function CdnAssetsView() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefix = normalizeBrowsePrefix(searchParams.get('prefix'));
  const profileFromUrl = searchParams.get('profile')?.trim() ?? '';

  const [profiles, setProfiles] = useState<CdnProfile[]>([]);
  const [profileId, setProfileId] = useState<string>(profileFromUrl);
  const [objects, setObjects] = useState<CdnObject[]>([]);
  const [folderPrefixes, setFolderPrefixes] = useState<string[]>([]);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadPath, setUploadPath] = useState(prefix);
  const [dragOver, setDragOver] = useState(false);
  const [renameTarget, setRenameTarget] = useState<CdnObject | null>(null);
  const [renameFilename, setRenameFilename] = useState('');
  const [renaming, setRenaming] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const wroteProfileRef = useRef(false);

  const activeProfile = useMemo(
    () => profiles.find((p) => p.id === profileId) ?? profiles[0] ?? null,
    [profiles, profileId],
  );

  const breadcrumbs = useMemo(() => {
    if (!prefix) return [{ label: 'Root', path: '' }];
    const parts = prefix.replace(/\/$/, '').split('/');
    const crumbs: { label: string; path: string }[] = [{ label: 'Root', path: '' }];
    let acc = '';
    for (const part of parts) {
      acc = acc ? `${acc}${part}/` : `${part}/`;
      crumbs.push({ label: part, path: acc });
    }
    return crumbs;
  }, [prefix]);

  const openFolder = useCallback(
    (nextPrefix: string, opts?: { profileId?: string; replace?: boolean }) => {
      const nextProfile = opts?.profileId ?? profileId;
      navigate(
        {
          pathname: '/cdn',
          search: cdnFolderSearch({
            prefix: nextPrefix,
            profileId: profiles.length > 1 ? nextProfile : undefined,
          }),
        },
        { replace: opts?.replace === true },
      );
    },
    [navigate, profileId, profiles.length],
  );

  const loadProfiles = useCallback(async () => {
    const list = await listCdnProfiles();
    const fromUrl = new URLSearchParams(window.location.search).get('profile')?.trim() ?? '';
    setProfiles(list);
    setProfileId((current) => {
      if (fromUrl && list.some((p) => p.id === fromUrl)) return fromUrl;
      if (current && list.some((p) => p.id === current)) return current;
      return list[0]?.id || '';
    });
    return list;
  }, []);

  const loadObjects = useCallback(
    async (opts?: { append?: boolean; token?: string | null }) => {
      if (!profileId) return;
      const append = opts?.append === true;
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setError(null);
      }
      try {
        const result = await listCdnObjects({
          profileId,
          prefix,
          continuationToken: opts?.token ?? undefined,
        });
        setObjects((prev) => (append ? [...prev, ...result.objects] : result.objects));
        setFolderPrefixes(result.prefixes);
        setNextToken(result.nextToken);
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : 'Failed to load CDN objects';
        captureEdgeFunctionFailure('cdn-assets', 'list', e, { profileId, prefix });
        setError(msg);
        if (!append) {
          setObjects([]);
          setFolderPrefixes([]);
          setNextToken(null);
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [profileId, prefix],
  );

  useEffect(() => {
    void (async () => {
      try {
        await loadProfiles();
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : 'Failed to load CDN profiles';
        captureEdgeFunctionFailure('cdn-assets', 'listProfiles', e);
        setError(msg);
        setLoading(false);
      }
    })();
  }, [loadProfiles]);

  useEffect(() => {
    if (!profileId) return;
    void loadObjects();
  }, [profileId, prefix, loadObjects]);

  useEffect(() => {
    setUploadPath(prefix);
  }, [prefix]);

  useEffect(() => {
    if (
      profileFromUrl &&
      profileFromUrl !== profileId &&
      profiles.some((p) => p.id === profileFromUrl)
    ) {
      setProfileId(profileFromUrl);
    }
  }, [profileFromUrl, profileId, profiles]);

  useEffect(() => {
    if (wroteProfileRef.current) return;
    if (profiles.length <= 1 || !profileId) return;
    wroteProfileRef.current = true;
    if (profileFromUrl === profileId) return;
    openFolder(prefix, { replace: true });
  }, [profiles.length, profileId, profileFromUrl, prefix, openFolder]);

  const handleCopyUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('URL copied');
    } catch {
      toast.error('Could not copy URL');
    }
  };

  const handleDelete = async (obj: CdnObject) => {
    if (!profileId) return;
    const ok = window.confirm(`Delete ${obj.key}? This cannot be undone.`);
    if (!ok) return;
    try {
      await deleteCdnObject({ profileId, key: obj.key });
      toast.success('Deleted');
      void loadObjects();
    } catch (e: unknown) {
      captureEdgeFunctionFailure('cdn-assets', 'delete', e, { key: obj.key });
      toast.error(e instanceof Error ? e.message : 'Delete failed');
    }
  };

  const openRename = (obj: CdnObject) => {
    setRenameTarget(obj);
    setRenameFilename(objectFilename(obj.key));
  };

  const closeRename = () => {
    if (renaming) return;
    setRenameTarget(null);
    setRenameFilename('');
  };

  const handleRename = async () => {
    if (!profileId || !renameTarget) return;
    const toKey = joinObjectKey(objectDir(renameTarget.key), renameFilename);
    if (!toKey) {
      toast.error('Enter a valid filename');
      return;
    }
    if (toKey === renameTarget.key) {
      closeRename();
      return;
    }
    setRenaming(true);
    try {
      const object = await renameCdnObject({
        profileId,
        fromKey: renameTarget.key,
        toKey,
      });
      toast.success('Renamed — new URL copied');
      setRenameTarget(null);
      setRenameFilename('');
      void loadObjects();
      void handleCopyUrl(object.publicUrl);
    } catch (e: unknown) {
      captureEdgeFunctionFailure('cdn-assets', 'rename', e, { fromKey: renameTarget.key });
      toast.error(e instanceof Error ? e.message : 'Rename failed');
    } finally {
      setRenaming(false);
    }
  };

  const handleUploadFiles = useCallback(
    async (files: FileList | File[] | null) => {
      if (!files?.length || !profileId) return;
      setUploading(true);
      const base = uploadPath.replace(/^\//, '').replace(/\/+$/, '');
      let success = 0;
      for (const file of Array.from(files)) {
        if (!file.type.startsWith('image/')) {
          toast.error(`${file.name} is not an image`);
          continue;
        }
        if (file.size > MAX_UPLOAD_MB * 1024 * 1024) {
          toast.error(`${file.name} exceeds ${MAX_UPLOAD_MB}MB limit`);
          continue;
        }
        const key = base ? `${base}/${file.name}` : file.name;
        try {
          await uploadCdnObject({ profileId, key, file });
          success += 1;
        } catch (e: unknown) {
          if (cdnObjectAlreadyExists(e)) {
            const replace = window.confirm(
              `${file.name} is already on the CDN. Replace it with this file? The public URL stays the same.`,
            );
            if (!replace) continue;
            try {
              await uploadCdnObject({ profileId, key, file, overwrite: true });
              success += 1;
              continue;
            } catch (retryError: unknown) {
              captureEdgeFunctionFailure('cdn-assets', 'upload', retryError, { key, overwrite: true });
              toast.error(
                retryError instanceof Error ? retryError.message : `Replace failed: ${file.name}`,
              );
              continue;
            }
          }
          captureEdgeFunctionFailure('cdn-assets', 'upload', e, { key });
          toast.error(e instanceof Error ? e.message : `Upload failed: ${file.name}`);
        }
      }
      setUploading(false);
      if (success > 0) {
        toast.success(`Uploaded ${success} image${success === 1 ? '' : 's'}`);
        void loadObjects();
      }
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [profileId, uploadPath, loadObjects],
  );

  const handlePaste = useCallback(
    (event: ClipboardEvent) => {
      if (!profileId || uploading) return;
      const items = event.clipboardData?.items;
      if (!items) return;
      const imageFiles: File[] = [];
      for (const item of Array.from(items)) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            const ext = item.type.split('/')[1]?.replace('jpeg', 'jpg') || 'png';
            imageFiles.push(
              new File([file], `paste-${Date.now()}.${ext}`, { type: item.type }),
            );
          }
        }
      }
      if (imageFiles.length > 0) {
        event.preventDefault();
        void handleUploadFiles(imageFiles);
      }
    },
    [profileId, uploading, handleUploadFiles],
  );

  useEffect(() => {
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [handlePaste]);

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div className="min-w-0 flex-1">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
            <Cloud className="w-8 h-8 mr-3 text-sky-500 shrink-0" />
            CDN Assets
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Upload and manage images on{' '}
            {activeProfile ? (
              <a
                href={activeProfile.baseUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-600 dark:text-emerald-400 hover:underline"
              >
                {activeProfile.label}
              </a>
            ) : (
              'Cloudflare R2'
            )}
            . Max {MAX_UPLOAD_MB}MB per image.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap">
          {profiles.length > 1 && (
            <select
              value={profileId}
              onChange={(e) => {
                const nextProfile = e.target.value;
                setProfileId(nextProfile);
                openFolder('', { profileId: nextProfile, replace: true });
              }}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
            >
              {profiles.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
          )}
          <button
            type="button"
            onClick={() => void loadObjects()}
            disabled={loading || !profileId}
            title="Refresh"
            aria-label="Refresh"
            className="inline-flex items-center justify-center p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || !profileId}
            title="Upload image"
            aria-label="Upload image"
            className="inline-flex items-center justify-center p-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {uploading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Upload className="w-4 h-4" />
            )}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            className="hidden"
            onChange={(e) => void handleUploadFiles(e.target.files)}
          />
        </div>
      </div>

      <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="min-w-0 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-3">
          <div className="flex flex-wrap items-center gap-2 text-sm mb-2">
            {breadcrumbs.map((crumb, i) => (
              <span key={crumb.path || 'root'} className="flex items-center gap-2">
                {i > 0 && <span className="text-gray-400">/</span>}
                <button
                  type="button"
                  onClick={() => openFolder(crumb.path)}
                  className={`hover:text-emerald-600 dark:hover:text-emerald-400 ${
                    i === breadcrumbs.length - 1
                      ? 'font-medium text-gray-900 dark:text-white'
                      : 'text-gray-600 dark:text-gray-400'
                  }`}
                >
                  {crumb.label}
                </button>
              </span>
            ))}
          </div>
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
            Folder
          </label>
          <input
            type="text"
            value={uploadPath}
            onChange={(e) => setUploadPath(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                openFolder(uploadPath);
              }
            }}
            placeholder="e.g. PNG/ or subscribers/"
            title="Enter opens this folder — refresh keeps you here"
            className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm font-mono"
          />
        </div>

        <div
          role="button"
          tabIndex={0}
          onClick={() => !uploading && fileInputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              if (!uploading) fileInputRef.current?.click();
            }
          }}
          onDragEnter={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            if (!e.currentTarget.contains(e.relatedTarget as Node)) {
              setDragOver(false);
            }
          }}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            void handleUploadFiles(e.dataTransfer.files);
          }}
          className={`min-w-0 w-full rounded-xl border-2 border-dashed transition-colors cursor-pointer flex flex-col items-center justify-center gap-1 px-4 py-3 text-center ${
            dragOver
              ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
              : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 hover:border-emerald-400 dark:hover:border-emerald-600'
          } ${uploading ? 'opacity-60 pointer-events-none' : ''}`}
        >
          {uploading ? (
            <Loader2 className="w-5 h-5 text-emerald-500 animate-spin" />
          ) : (
            <Upload className="w-5 h-5 text-emerald-500" />
          )}
          <p className="text-xs font-medium text-gray-900 dark:text-white leading-snug">
            {uploading ? 'Uploading…' : 'Drop or click'}
          </p>
          <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-snug">
            paste · max {MAX_UPLOAD_MB}MB
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 text-sm">
          {error}
          {error.includes('R2 credentials') && (
            <p className="mt-2 text-red-700 dark:text-red-300">
              Set R2 secrets on the Supabase project, then redeploy{' '}
              <code className="font-mono text-xs">cdn-assets</code>.
            </p>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Loading…
        </div>
      ) : (
        <>
          {folderPrefixes.length > 0 && (
            <div className="mb-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {folderPrefixes.map((folderPrefix) => (
                <button
                  key={folderPrefix}
                  type="button"
                  onClick={() => openFolder(folderPrefix)}
                  className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-emerald-400 dark:hover:border-emerald-600 text-left"
                >
                  <Folder className="w-5 h-5 text-amber-500 shrink-0" />
                  <span className="font-medium text-gray-900 dark:text-white truncate">
                    {folderName(folderPrefix)}
                  </span>
                </button>
              ))}
            </div>
          )}

          {objects.length === 0 && folderPrefixes.length === 0 ? (
            <div className="text-center py-16 text-gray-500 dark:text-gray-400">
              No images in this folder. Drop one on the right or open a subfolder.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Preview
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Key
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Size
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Modified
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {objects.map((obj) => {
                    const filename = obj.key.split('/').pop() ?? obj.key;
                    return (
                      <tr key={obj.key} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td className="px-4 py-3 w-16">
                          {isImageKey(obj.key) ? (
                            <img
                              src={obj.publicUrl}
                              alt=""
                              className="w-10 h-10 object-cover rounded border border-gray-200 dark:border-gray-600"
                              loading="lazy"
                            />
                          ) : (
                            <div className="w-10 h-10 flex items-center justify-center rounded bg-gray-100 dark:bg-gray-800">
                              <ImageIcon className="w-4 h-4 text-gray-400" />
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-mono text-sm text-gray-900 dark:text-white break-all">
                            {filename}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-md">
                            {obj.key}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                          {formatBytes(obj.size)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                          {obj.lastModified
                            ? new Date(obj.lastModified).toLocaleString()
                            : '—'}
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <div className="inline-flex items-center gap-1">
                            <button
                              type="button"
                              title="Rename"
                              onClick={() => openRename(obj)}
                              className="p-2 rounded-lg text-gray-500 hover:text-amber-600 hover:bg-gray-100 dark:hover:bg-gray-800"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              title="Copy URL"
                              onClick={() => void handleCopyUrl(obj.publicUrl)}
                              className="p-2 rounded-lg text-gray-500 hover:text-emerald-600 hover:bg-gray-100 dark:hover:bg-gray-800"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                            <a
                              href={obj.publicUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="Open"
                              className="p-2 rounded-lg text-gray-500 hover:text-sky-600 hover:bg-gray-100 dark:hover:bg-gray-800"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                            <button
                              type="button"
                              title="Delete"
                              onClick={() => void handleDelete(obj)}
                              className="p-2 rounded-lg text-gray-500 hover:text-red-600 hover:bg-gray-100 dark:hover:bg-gray-800"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {nextToken && (
            <div className="mt-4 flex justify-center">
              <button
                type="button"
                disabled={loadingMore}
                onClick={() => void loadObjects({ append: true, token: nextToken })}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
              >
                {loadingMore && <Loader2 className="w-4 h-4 animate-spin" />}
                Load more
              </button>
            </div>
          )}
        </>
      )}

      {renameTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="cdn-rename-title"
            className="w-full max-w-md rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-xl p-5"
          >
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <h2 id="cdn-rename-title" className="text-lg font-semibold text-gray-900 dark:text-white">
                  Rename file
                </h2>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 font-mono break-all">
                  {objectDir(renameTarget.key) || '(root)/'}
                </p>
              </div>
              <button
                type="button"
                onClick={closeRename}
                disabled={renaming}
                className="p-1 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Filename
            </label>
            <input
              type="text"
              value={renameFilename}
              onChange={(e) => setRenameFilename(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void handleRename();
              }}
              autoFocus
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm font-mono"
            />
            <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
              The public URL will change. Update any links that point at the old path.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeRename}
                disabled={renaming}
                className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleRename()}
                disabled={renaming || !renameFilename.trim()}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
              >
                {renaming && <Loader2 className="w-4 h-4 animate-spin" />}
                Rename
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
